import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import { MAutocomplete, MDropdown } from 'common';
import { func, shape } from 'prop-types';
import { isNotEmpty } from 'utils/helpers/formValidations';
import { REQUIRED } from 'utils/helpers/validationMessages';
import { useUserRolePermissions } from 'hooks';
import * as R from 'ramda';
import {
  useGetCitiesAndZipsQuery,
  useGetClassesQuery,
  useGetStatesQuery,
} from 'feature/loading-management/customer-authorizations/customerAuthorizationsEndpoints';
import AuthorizationVolumes from './AuthorizationVolumes';

const CustomerInformation = ({ customerAuth, validation, setCustomerAuth, setValidation }) => {
  const { currentAffiliate } = useUserRolePermissions();
  const {
    address,
    address2,
    city,
    customerIdName,
    state,
    customerName,
    customerClassId,
    customerClassName,
    zipCode,
    phoneNumber,
  } = customerAuth;

  const classType = {
    customerClassId,
    customerClassName,
  };

  const customerValidation = R.omit(['controlNumbers'], validation);

  const hasValidationErrors = !!R.length(
    Object.keys(customerValidation).filter(key => R.not(R.isEmpty(customerValidation[key])))
  );

  const params = {
    supplier: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
  };

  const { data: allStates, isLoading: isLoadingStates } = useGetStatesQuery();
  const states = allStates ? R.map(({ id, name }) => ({ label: name, value: id }), allStates) : [];
  const skipClassFetch = R.isNil(currentAffiliate);
  const { data: allClasses = [], isLoading: isLoadingClasses } = useGetClassesQuery(
    { params },
    {
      skip: skipClassFetch,
    }
  );

  const foundState = states.find(found => found.value === state);
  const skipCityZipFetch = R.or(R.isNil(foundState), R.isEmpty(foundState));
  const stateParmas = { state: foundState?.label };
  const { data: citiesZips, isLoading: isLoadingCityZip } = useGetCitiesAndZipsQuery(
    { params: stateParmas },
    {
      skip: skipCityZipFetch,
    }
  );

  const cities = R.not(R.isNil(R.prop('citiesAndZips', citiesZips)))
    ? R.map(item => ({ label: item.city, value: item.city }), citiesZips.citiesAndZips)
    : [];

  const zips =
    R.not(R.isNil(city)) && R.not(R.isNil(R.prop('citiesAndZips', citiesZips)))
      ? R.map(
          zip => ({ label: zip, value: zip }),
          citiesZips.citiesAndZips.find(foundCity => foundCity.city === city)?.zips ?? []
        )
      : [];

  const defaultToEmpty = R.defaultTo('');

  const validateRequired = R.cond([
    [R.isEmpty, R.always(REQUIRED)],
    [R.isNil, R.always(REQUIRED)],
    [R.T, R.always('')],
  ]);

  const isEmptyValue = R.cond([
    [R.isEmpty, R.T],
    [R.isNil, R.T],
    [R.T, R.F],
  ]);

  useEffect(() => {
    if (R.not(R.isEmpty(customerAuth))) {
      if (isEmptyValue(customerName)) {
        R.compose(
          setValidation,
          R.assoc('customerName'),
          validateRequired,
          R.prop('customerName')
        )(customerAuth);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerAuth]);

  const handleStateChange = val => {
    setCustomerAuth(
      R.evolve(
        {
          city: R.always(undefined),
          state: R.always(val),
          zipCode: R.always(undefined),
        },
        customerAuth
      )
    );
    setValidation(R.assoc('state', validateRequired(val), validation));
  };

  const handleCityChange = val => {
    setCustomerAuth(
      R.evolve({
        city: R.always(val),
        zipCode: R.always(undefined),
      }),
      customerAuth
    );
    setValidation(R.assoc('city', validateRequired(val), validation));
  };

  const handleZipChange = val => {
    setCustomerAuth(R.assoc('zipCode', val, customerAuth));
    setValidation(R.assoc('zipCode', validateRequired(val), validation));
  };

  const handleReqFormChange = e => {
    const value = R.path(['target', 'value'], e);
    const name = R.path(['target', 'name'], e);
    setCustomerAuth(R.assoc(name, value, customerAuth));
  };

  const [customerNameExceedsLimit, setCustomerNameExceedsLimit] = useState(false);
  const handleCustomerNameReqFormChange = e => {
    const value = R.path(['target', 'value'], e);
    const name = R.path(['target', 'name'], e);
    if (value.length > 40) {
      setCustomerNameExceedsLimit(true);
    } else {
      setCustomerNameExceedsLimit(false);
      setCustomerAuth(R.assoc(name, value, customerAuth));
    }
    const errorMessage = validateRequired(value);
    setValidation(R.assoc(name, errorMessage, validation));
  };

  const handleNonReqFormChange = e =>
    setCustomerAuth(
      R.assoc(R.path(['target', 'name'], e), R.path(['target', 'value'], e), customerAuth)
    );

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        aria-controls="customer-customerAuth-content"
        expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="body2">Customer Information</Typography>
          {hasValidationErrors && <ErrorRoundedIcon sx={{ color: 'error.main' }} />}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <AuthorizationVolumes
          dataPath={[]}
          info={customerAuth}
          setInfo={setCustomerAuth}
          setValidation={setValidation}
          validation={validation}
        />
        <Divider sx={{ mb: 3, mt: 2 }} />
        <Stack direction="column" spacing={2}>
          <TextField
            disabled={R.not(R.isNil(customerIdName))}
            label="Customer ID"
            name="customerId"
            onChange={() => {}}
            value={defaultToEmpty(customerIdName)}
          />
          <TextField
            error={isNotEmpty(validation.customerName) || customerNameExceedsLimit}
            helperText={customerNameExceedsLimit ? '40 Characters Max' : validation.customerName}
            label="Customer Name"
            name="customerName"
            onBlur={() => {
              setCustomerNameExceedsLimit(false);
              const errorMessage = validateRequired(customerName);
              setValidation(R.assoc('customerName', errorMessage, validation));
            }}
            onChange={handleCustomerNameReqFormChange}
            required
            value={defaultToEmpty(customerName)}
          />
          <TextField
            label="Street Address"
            name="address"
            onChange={handleReqFormChange}
            value={defaultToEmpty(address)}
          />
          <TextField
            helperText="(Optional)"
            label="Suite, Apt. #, PO Box"
            name="address2"
            onChange={handleNonReqFormChange}
            value={defaultToEmpty(address2)}
          />
          <Box
            sx={{
              display: 'flex',
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              flexDirection: { xs: 'column', lg: 'row' },
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              gridColumnGap: { xs: 0, lg: 16 },
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              gridRowGap: { xs: 16, lg: 0 },
            }}
          >
            <Box
              sx={{
                // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                flex: { xs: 1, lg: 3 },
              }}
            >
              <MDropdown
                id="state-select"
                items={states}
                label="State"
                loading={isLoadingStates}
                name="state"
                onChange={({ value }) => handleStateChange(value)}
                value={isLoadingStates ? '' : state}
              />
            </Box>
            <MAutocomplete
              // disabled={R.isNil(state)}
              fullWidth
              helperText={R.isNil(state) ? 'Please select state first' : ''}
              id="city"
              label="City"
              loading={isLoadingCityZip}
              name="city"
              onChange={(_, obj, reason) => {
                reason === 'clear'
                  ? setCustomerAuth(R.assoc('city', undefined, customerAuth))
                  : handleCityChange(obj.value);
              }}
              options={cities}
              sx={{
                // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                flex: { xs: 1, lg: 4 },
              }}
              value={defaultToEmpty(city)}
            />
            <MAutocomplete
              // disabled={R.isNil(city)}
              fullWidth
              helperText={R.isNil(city) ? 'Please select state & city first' : ''}
              id="zipCode"
              label="Zip Code"
              loading={isLoadingCityZip}
              name="zipCode"
              onChange={(_, obj, reason) => {
                reason === 'clear'
                  ? setCustomerAuth(R.assoc('zipCode', undefined, customerAuth))
                  : handleZipChange(obj.value);
              }}
              options={zips}
              sx={{
                // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                flex: { xs: 1, lg: 3 },
              }}
              value={defaultToEmpty(zipCode)}
            />
          </Box>
          <TextField
            helperText="(Optional)"
            label="Phone Number"
            name="phoneNumber"
            onChange={handleNonReqFormChange}
            value={defaultToEmpty(phoneNumber)}
          />
          <Autocomplete
            data-testid="class"
            getOptionLabel={o => defaultToEmpty(R.prop('customerClassName', o))}
            isOptionEqualToValue={o => o.customerClassId === customerClassId}
            loading={isLoadingClasses}
            onChange={(_, obj, reason) =>
              R.ifElse(
                R.equals('clear'),
                R.always(setCustomerAuth(R.assoc('class', {}, customerAuth))),
                R.always(
                  setCustomerAuth(
                    R.evolve({
                      customerClassId: R.always(R.prop('customerClassId', obj)),
                      customerClassName: R.always(R.prop('customerClassName', obj)),
                    }),
                    customerAuth
                  )
                )
              )(reason)
            }
            options={allClasses}
            renderInput={p => <TextField {...p} helperText="(Optional)" label="Class" />}
            value={R.and(R.isNil(customerClassId), R.isNil(customerClassName)) ? null : classType}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

CustomerInformation.propTypes = {
  customerAuth: shape({}),
  setCustomerAuth: func.isRequired,
  setValidation: func.isRequired,
  validation: shape({}).isRequired,
};

CustomerInformation.defaultProps = {
  customerAuth: {},
};

export default CustomerInformation;
